import React, { useCallback, useState } from "react";
import { isEmpty, isEqual } from "lodash";
import { useStateSelector } from "utils/field";
import { useModelCodeLib } from "utils/codes/modelCodeLib";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Tooltip from "components/Tooltip";
import Checkbox from "components/Input/Checkbox";

import { required, requiredCodeRef, getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { getOptions, getBaseUnits, getUnitOptions, getFoundationConfigParams } from "utils/fields";

import { actions } from "features/Model/Enclosure/_ducks";

import Add from "assets/images/icons/JSX/Add";
import Book from "assets/images/icons/JSX/Book";
import Edit from "assets/images/icons/JSX/Edit";

import classes from "./style.module.scss";
import globalStyles from "components/globalStyles.module.scss";
import { isOrgCodeRef } from "utils/codes/refs";

const { setInitCode, updateCodeDrawer, updateCompositeCalc } = actions;

const basementWallTypeValidation = [required, ...getValidation("basementIntAddedIns")];
const basementExtWallTypeValidation = [required, ...getValidation("basementExtAddedIns")];
const lintelTypeValidation = getValidation("wallLintelInsType");

export const CodeFooter = ({ toggleDrawer, setInitCode, codeType }) => (
    <div
        className={classes.codeFooter}
        onClick={async () => {
            await setInitCode({}, codeType);
            toggleDrawer(true);
        }}
    >
        <span>Create New Code</span>
        <Add />
    </div>
);

const codeSort = (a, b) => {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
};

const LabelActionComponent = ({ codeRef, handleEdit, codeType }) => (
    <>
        <div
            className={globalStyles.editCode}
            data-tip="Edit Code"
            data-for={codeRef}
            onClick={(event) => handleEdit(event, codeRef, codeType)}
        >
            <Edit />
            <Tooltip id={codeRef} className={globalStyles.fieldTooltip} place="left" />
        </div>
    </>
);

// BasementWall Ins. Type codes found in code library
export const basementWallTypeLibCodesMap = (libCodes, handleEditLibBasementWallCode) =>
    Object.keys(libCodes)
        .map((codeRef) => {
            const { label = "" } = libCodes[codeRef] || {};
            const [standardCode] = codeRef.split("BasementWall-U");

            return {
                label: label,
                labelIcon: Book,
                labelTag: isOrgCodeRef(codeRef) ? "Organization" : !standardCode ? "User Defined" : "",
                labelActionComponent: () =>
                    handleEditLibBasementWallCode ? (
                        <LabelActionComponent
                            codeRef={codeRef}
                            handleEdit={handleEditLibBasementWallCode}
                            codeType={standardCode ? "S" : "U"}
                        />
                    ) : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// BasementWall Ins. Type codes found in model, filtered by those in library
export const basementWallTypeModelCodesMap = (modelCodes, libCodes, handleEditModelBasementWallCode) =>
    Object.values(modelCodes)
        .filter(({ codeRef }) => {
            const [component] = codeRef.split("-");
            if (!!libCodes[codeRef]) {
                return (
                    (!libCodes[codeRef] && component === "BasementWall") ||
                    !isEqual(modelCodes[codeRef].layers, libCodes[codeRef].layers)
                );
            }
            return !libCodes[codeRef] && component === "BasementWall";
        })
        .map(({ label, codeRef }) => {
            const [standardCode] = codeRef.split("BasementWall-U");

            return {
                label,
                labelTag: isOrgCodeRef(codeRef) ? "Organization" : !standardCode ? "User Defined" : "",
                labelActionComponent: () => (
                    <LabelActionComponent
                        codeRef={codeRef}
                        handleEdit={handleEditModelBasementWallCode}
                        codeType={standardCode ? "S" : "U"}
                    />
                ),
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// Lintel Type codes found in code library
export const lintelTypeLibCodesMap = (libLintelCodes, handleEditLibLintelCode) =>
    Object.keys(libLintelCodes)
        .map((codeRef) => {
            const { label = "" } = libLintelCodes[codeRef] || {};
            const [standardCode] = codeRef.split("Lintel-U");

            return {
                label: label,
                labelIcon: Book,
                labelActionComponent:
                    standardCode && handleEditLibLintelCode
                        ? () => <LabelActionComponent codeRef={codeRef} handleEdit={handleEditLibLintelCode} />
                        : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// Lintel Type codes found in model, filtered by those in library
export const lintelTypeModelCodesMap = (modelCodes, libCodes, handleEditModelLintelCode) =>
    Object.values(modelCodes)
        .filter(({ codeRef }) => {
            const [component] = codeRef.split("-");
            if (!!libCodes[codeRef]) {
                return (
                    (!libCodes[codeRef] && component === "Lintel") ||
                    !isEqual(modelCodes[codeRef].layers, libCodes[codeRef].layers)
                );
            }
            return !libCodes[codeRef] && component === "Lintel";
        })
        .map(({ label, codeRef }) => {
            const [standardCode] = codeRef.split("Lintel-U");

            return {
                label,
                labelActionComponent: standardCode
                    ? () => <LabelActionComponent codeRef={codeRef} handleEdit={handleEditModelLintelCode} />
                    : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

const WallConstruction = React.memo(
    ({
        accessor,
        id,
        change,
        setInitCode,
        updateCodeDrawer,
        isConcrete,
        updateCompositeCalc,
        disabledCodeEdit = false,
        isUpgrade = false,
        formName,
        canBeEditedFromParametric = true,
        isCompact = false,
        selectedUpgrade,
        componentAccessor = accessor,
        isParametricAnalysis = false,
        isCommunity = false,
        codeRefs = [],
    }) => {
        // Used to hide warning when code is reselected but not yet added to ecmInputMap
        const [isTouched, setIsTouched] = useState(false);
        const [isTouchedLintel, setIsTouchedLintel] = useState(false);

        const { BasementWall: { codes: libCodes = {} } = {}, Lintel: { codes: libLintelCodes = {} } = {} } =
            useModelCodeLib() || {};
        const { calculatingRValue = {} } = useStateSelector("enclosure") || {};
        const { currentPackage = {} } = useStateSelector("upgradeLibrary");
        const { parametricAnalysis = {} } = useStateSelector("parametricAnalysis") || {};
        const { community = {} } = useStateSelector("community") || {};
        const { baseModelId = "" } = useStateSelector(`commUpgrades`) || {};
        const { modelData = {} } = useStateSelector(`model`) || {};

        const ecmInputMap = parametricAnalysis?.ecmInputMap || community?.ecmInputMap || {};

        const allEcmInputMapUpgradesArr = ecmInputMap
            ? Object.keys(ecmInputMap)
                .map((key) =>
                    Object.entries(ecmInputMap[key])
                        .filter(([k, _]) => k !== "None")
                        .map(([k, v]) => ({ [k]: v }))
                )
                .flat()
            : [];

        const allEcmInputMapUpgrades = allEcmInputMapUpgradesArr.reduce((acc, curr) => ({ ...acc, ...curr }), {});

        const modelCodes =
            allEcmInputMapUpgrades[selectedUpgrade]?.packageValues?.codes ||
            currentPackage?.upgrades?.[selectedUpgrade]?.codes ||
            modelData?.codes ||
            {};

        const addUserSpecifiedRVal = useStateSelector(`${accessor}.addUserSpecifiedRVal`) || false;

        const isAddUserSpecifiedRVal = isParametricAnalysis ? addUserSpecifiedRVal : false;

        const modelUnits = modelData?.uiSettings?.primaryUnits || {};
        const { type: basementInsConfig } = useStateSelector(`${componentAccessor}.configuration`) || {};

        const { codeRef: selectedCodeRef } = useStateSelector(`${accessor}.wall.intAddedInsType`) || {};
        const selectedNomRValue = useStateSelector(`${accessor}.wall.intAddedInsType_nomRVal`) || 0;
        const selectedEffRValue = useStateSelector(`${accessor}.wall.intAddedInsType_effRVal`) || 0;
        const compositeIntEffRValue = useStateSelector(`${accessor}.wall.intAddedInsType_composite.effRVal`) || 0;
        const compositeExtEffRValue = useStateSelector(`${accessor}.wall.extAddedInsType_composite.effRVal`) || 0;
        const basementWallCodeWarning = useStateSelector(`${accessor}.wall.intAddedInsType_warning`) || "";
        const { codeRef: selectedLintelCodeRef } = useStateSelector(`${accessor}.wall.lintelInsType`) || {};
        const { id: extRValId, value: extRValue } = useStateSelector(`${accessor}.wall.extAddedInsType`) || {};
        const intRValUnits = useStateSelector(`${accessor}.wall.intAddedInsType_u`);
        const extRValUnits = useStateSelector(`${accessor}.wall.extAddedInsType_u`);
        const { [id]: { basementWallInsType: isCalculatingRValue = false } = {} } = calculatingRValue;

        //Determine if selectedCodeRef is in modelCodes (attached properly)
        const modelCodesContainsSelected =
            Object.keys(modelCodes).includes(selectedCodeRef) || selectedCodeRef === "UserSpecified" || isTouched;

        const modelCodesContainsSelectedLintel =
            Object.keys(modelCodes).includes(selectedLintelCodeRef) || selectedLintelCodeRef === "UserSpecified" || isTouchedLintel;

        const handleTypeChange = useCallback(
            ({ codeRef = "" }) => {
                if (!codeRef) return;
                if (codeRef === "UserSpecified") {
                    change(`${accessor}.wall.intAddedInsType_warning`, "");
                    change(`${accessor}.wall.intAddedInsType_composite`, {});
                    return;
                }

                const modelMatch = modelCodes[codeRef] || {};
                if (!isEmpty(modelMatch)) {
                    change(`${accessor}.wall.intAddedInsType_nomRVal`, modelMatch.nominalRValue || 0);
                    change(`${accessor}.wall.intAddedInsType_effRVal`, modelMatch.nominalRValue || 0);
                    change(`${accessor}.wall.intAddedInsType_warning`, modelMatch.warningType || "");
                } else {
                    const { nominalRValue = 0, warningType = "" } = libCodes[codeRef] || {};
                    change(`${accessor}.wall.intAddedInsType_nomRVal`, nominalRValue || 0);
                    change(`${accessor}.wall.intAddedInsType_effRVal`, nominalRValue || 0);
                    change(`${accessor}.wall.intAddedInsType_warning`, warningType || "");

                    if (isCommunity || isParametricAnalysis) {
                        setIsTouched(true);
                    } else {
                        change(`model.modelData.codes.${codeRef}`, { ...libCodes[codeRef], codeRef });
                    }
                }
                change(`${accessor}.wall.intAddedInsType_composite`, {});

                //Redundancy
                change(`model.stashedCodes.BasementWall.${accessor.split(".").slice(-1)[0]}`, codeRef);
            },
            [libCodes, modelCodes, change, accessor, selectedNomRValue, isCommunity, isParametricAnalysis]
        );

        const handleLintelTypeChange = useCallback(
            ({ codeRef = "" }) => {
                if (!codeRef) return;
                if (codeRef === "UserSpecified") {
                    return;
                }

                const modelMatch = modelCodes[codeRef] || {};
                if (isEmpty(modelMatch)) {
                    if (isCommunity || isParametricAnalysis) {
                        setIsTouchedLintel(true);
                    } else {
                        change(`model.modelData.codes.${codeRef}`, { ...libLintelCodes[codeRef], codeRef });
                    }
                }

                change(`model.stashedCodes.Lintel.${accessor.split(".").slice(-1)[0]}`, codeRef);
            },
            [change, accessor, modelCodes, libLintelCodes, isCommunity, baseModelId]
        );

        const handleEditModelBasementWallCode = useCallback(
            async (event, codeRef, codeType) => {
                event.stopPropagation(); //Don't let click event bubble up to parent
                const initCodeType = codeType === "U" ? "udefStandard" : "basementWallInsType";
                const code = modelCodes[codeRef];
                await setInitCode(code, initCodeType);
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Basement Wall",
                    isEditing: true,
                    componentType: "BasementWall",
                    codeType: codeType,
                    fieldAccessor: `${accessor}.wall.intAddedInsType`,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, modelCodes, setInitCode, updateCodeDrawer]
        );

        const handleEditLibBasementWallCode = disabledCodeEdit
            ? null
            : useCallback(
                async (event, codeRef, codeType) => {
                    event.stopPropagation(); //Don't let click event bubble up to parent
                    const initCodeType = codeType === "U" ? "udefStandard" : "basementWallInsType";
                    const code = {
                        ...libCodes[codeRef],
                        codeRef: codeRef,
                        isLibCode: true,
                    };
                    await setInitCode(code, initCodeType);
                    updateCodeDrawer({
                        isOpen: true,
                        codeName: "Basement Wall",
                        isEditing: true,
                        componentType: "BasementWall",
                        codeType: codeType,
                        fieldAccessor: `${accessor}.wall.intAddedInsType`,
                        componentId: id,
                        id: codeRef,
                        lastEdited: code.lastEdited || "",
                    });
                },
                [accessor, id, libCodes, setInitCode, updateCodeDrawer]
            );

        const handleEditModelLintelCode = useCallback(
            async (event, codeRef) => {
                event.stopPropagation(); //Don't let click event bubble up to parent
                const code = modelCodes[codeRef];
                await setInitCode(code, "lintelInsType");
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Lintel",
                    isEditing: true,
                    componentType: "Lintel",
                    codeType: "S",
                    fieldAccessor: `${accessor}.wall.lintelInsType`,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, modelCodes, setInitCode, updateCodeDrawer]
        );

        const handleEditLibLintelCode = disabledCodeEdit
            ? null
            : useCallback(
                async (event, codeRef) => {
                    event.stopPropagation(); //Don't let click event bubble up to parent
                    const code = {
                        ...libLintelCodes[codeRef],
                        codeRef: codeRef,
                        isLibCode: true,
                    };
                    await setInitCode(code, "lintelInsType");
                    updateCodeDrawer({
                        isOpen: true,
                        codeName: "Lintel",
                        isEditing: true,
                        componentType: "Lintel",
                        codeType: "S",
                        fieldAccessor: `${accessor}.wall.lintelInsType`,
                        componentId: id,
                        id: codeRef,
                        lastEdited: code.lastEdited || "",
                    });
                },
                [accessor, id, libLintelCodes, setInitCode, updateCodeDrawer]
            );

        const { extIns: exteriorInsAllowed, intIns: interiorInsAllowed } = getFoundationConfigParams({
            fieldKey: "basementInsConfig",
            configKey: basementInsConfig,
        });

        const gridTemplate = isCompact ? "1fr 1fr 1fr 1fr 1fr" : isUpgrade ? "2fr 1fr 2fr" : "31.85% 23.25% 31.85%";

        return (
            <>
                <InputRow gridTemplate={gridTemplate} compact={isCompact}>
                    <Field
                        component={Select}
                        type="number"
                        name={`${accessor}.wall.intAddedInsType`}
                        options={[
                            {
                                label: "User Specified",
                                value: {
                                    codeLabel: "User Specified",
                                    codeRef: "UserSpecified",
                                },
                            },
                            ...basementWallTypeModelCodesMap(modelCodes, libCodes, handleEditModelBasementWallCode),
                            ...basementWallTypeLibCodesMap(libCodes, handleEditLibBasementWallCode),
                        ].map((option) => ({
                            ...option,
                            disabled: codeRefs.includes(option.value.codeRef),
                        }))}
                        label="Interior Basement Wall Insulation"
                        placeholder="Choose Interior Insulation Type"
                        searchPlaceholder="Search Basement Wall Codes"
                        search={true}
                        footer={() => (
                            <CodeFooter
                                toggleDrawer={(value) =>
                                    updateCodeDrawer({
                                        isOpen: value,
                                        codeName: "Basement Wall",
                                        isEditing: false,
                                        componentType: "BasementWall",
                                        codeType: "S",
                                        fieldAccessor: `${accessor}.wall.intAddedInsType`,
                                        componentId: id,
                                    })
                                }
                                setInitCode={setInitCode}
                                codeType="basementWallInsType"
                            />
                        )}
                        className={classes.basementWallType}
                        disabled={!interiorInsAllowed || !canBeEditedFromParametric}
                        isLoading={isCalculatingRValue}
                        onChange={handleTypeChange}
                        validate={requiredCodeRef}
                        compact={isCompact}
                        warning={!modelCodesContainsSelected ? "codeDataMissing" : null}
                    />
                    <div className={classes.insulationCalcWrapper}>
                        {selectedCodeRef === "UserSpecified" ? (
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.wall.intAddedInsType_effRVal`}
                                label={"Effective R-Value"}
                                placeholder={0.0}
                                disabled={selectedCodeRef !== "UserSpecified" || !canBeEditedFromParametric}
                                validate={basementWallTypeValidation}
                                setTouched={true}
                                setValue={selectedEffRValue}
                                decimals={getDecimalPlaces("basementIntAddedIns")}
                                change={change}
                                units={{
                                    base: getBaseUnits("basementIntAddedIns", modelUnits),
                                    options: getUnitOptions("thermalResistance"),
                                    selected: intRValUnits,
                                    unitType: "thermalResistance",
                                    accessor: `${accessor}.wall.intAddedInsType_u`,
                                }}
                                onChange={(value) => {
                                    if (selectedCodeRef === "UserSpecified") {
                                        change(`${accessor}.wall.intAddedInsType_nomRVal`, value);
                                    }
                                    if (value !== compositeIntEffRValue) {
                                        change(`${accessor}.wall.intAddedInsType_composite`, {});
                                    }
                                }}
                                // info={"User specified values are assumed to be effective R-values."}
                                editToggle={() =>
                                    updateCompositeCalc({
                                        open: true,
                                        fieldId: "basementIntAddedIns",
                                        fieldAccessor: `${accessor}.wall.intAddedInsType`,
                                        isConcrete,
                                        rValUnits: intRValUnits,
                                    })
                                }
                                compact={isCompact}
                            />
                        ) : (
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.wall.intAddedInsType_nomRVal`}
                                label={"Nominal R-Value"}
                                placeholder={0.0}
                                disabled={true}
                                validate={basementWallTypeValidation}
                                setTouched={true}
                                decimals={getDecimalPlaces("basementIntAddedIns")}
                                change={change}
                                setValue={selectedNomRValue}
                                units={{
                                    base: getBaseUnits("basementIntAddedIns", modelUnits),
                                    options: getUnitOptions("thermalResistance"),
                                    selected: intRValUnits,
                                    unitType: "thermalResistance",
                                    accessor: `${accessor}.wall.intAddedInsType_u`,
                                }}
                                info={
                                    "Values calculated by the Code Selector are nominal R-values. HOT2000 re-calculates R-values based on the content of the code when running a simulation."
                                }
                                warning={basementWallCodeWarning}
                                isLoading={isCalculatingRValue}
                                compact={isCompact}
                            />
                        )}
                    </div>
                    <Field
                        component={Select}
                        type="number"
                        name={`${accessor}.wall.lintelInsType`}
                        options={[
                            {
                                label: "N/A",
                                value: {},
                            },
                            ...lintelTypeModelCodesMap(modelCodes, libLintelCodes, handleEditModelLintelCode),
                            ...lintelTypeLibCodesMap(libLintelCodes, handleEditLibLintelCode),
                        ]}
                        label="Lintel Type"
                        placeholder="Choose Lintel Type"
                        searchPlaceholder="Search Lintel Codes"
                        search={true}
                        validate={lintelTypeValidation}
                        footer={() => (
                            <CodeFooter
                                toggleDrawer={(value) =>
                                    updateCodeDrawer({
                                        isOpen: value,
                                        codeName: "Lintel",
                                        isEditing: false,
                                        componentType: "Lintel",
                                        codeType: "S",
                                        fieldAccessor: `${accessor}.wall.lintelInsType`,
                                        componentId: id,
                                    })
                                }
                                setInitCode={setInitCode}
                                codeType="basementLintelInsType"
                            />
                        )}
                        onChange={handleLintelTypeChange}
                        className={classes.lintelType}
                        disabled={!canBeEditedFromParametric}
                        compact={isCompact}
                        warning={!modelCodesContainsSelectedLintel ? "codeDataMissing" : null}
                    />
                    {/* </InputRow>
                <InputRow gridTemplate={gridTemplate} compact={isCompact}> */}
                    <Field
                        component={Select}
                        name={`${accessor}.wall.extAddedInsType`}
                        options={[
                            ...getOptions({ fieldKey: "basementExtAddedIns" }),
                            {
                                label: "User Specified",
                                value: {
                                    id: 10,
                                    value: extRValue, //need to get rValue for this to match
                                },
                            },
                        ].map((option) => ({
                            ...option,
                            disabled: codeRefs.includes(option.value.codeRef),
                        }))}
                        label="Exterior Basement Wall Insulation"
                        placeholder="Choose Exterior Insulation Type"
                        validate={basementExtWallTypeValidation}
                        disabled={!exteriorInsAllowed || !canBeEditedFromParametric}
                        onChange={() => {
                            change(`${accessor}.wall.extAddedInsType_composite`, {});
                        }}
                        compact={isCompact}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.wall.extAddedInsType.value`}
                        label="Effective R-Value"
                        placeholder={0.0}
                        disabled={extRValId !== 10 || !exteriorInsAllowed || !canBeEditedFromParametric}
                        validate={basementExtWallTypeValidation}
                        setTouched={true}
                        decimals={getDecimalPlaces("basementExtAddedIns")}
                        change={change}
                        setValue={extRValue}
                        units={{
                            base: getBaseUnits("basementExtAddedIns", modelUnits),
                            options: getUnitOptions("thermalResistance"),
                            selected: extRValUnits,
                            unitType: "thermalResistance",
                            accessor: `${accessor}.wall.extAddedInsType_u`,
                        }}
                        onChange={(value) => {
                            if (value !== compositeExtEffRValue) {
                                change(`${accessor}.wall.extAddedInsType_composite`, {});
                            }
                        }}
                        editToggle={
                            extRValId === 10
                                ? () =>
                                    updateCompositeCalc({
                                        open: true,
                                        fieldId: "basementExtAddedIns",
                                        fieldAccessor: `${accessor}.wall.extAddedInsType.value`,
                                        isConcrete,
                                        rValUnits: extRValUnits,
                                    })
                                : null
                        }
                        compact={isCompact}
                    />
                </InputRow>
                {isParametricAnalysis && (
                    <>
                        <InputRow gridTemplate="1fr" compact lastRow>
                            <Field
                                className={classes.bottomRowCheckbox}
                                component={Checkbox}
                                name={`${accessor}.addUserSpecifiedRVal`}
                                label="Add User Specified R-Value to All Components (Int. Wall)"
                                large
                                type="checkbox"
                                compact
                                onChange={(value) => {
                                    if (value) {
                                        change(`${accessor}.wall.intAddedInsType`, {
                                            codeRef: "UserSpecified",
                                            codeLabel: "User Specified",
                                        });
                                    }
                                }}
                                disabled={!canBeEditedFromParametric}
                            />
                        </InputRow>
                        {isAddUserSpecifiedRVal && (
                            <div
                                style={{
                                    backgroundColor: "aliceblue",
                                    borderRadius: "10px",
                                    padding: "0.75rem",
                                    lineHeight: "normal",
                                    fontSize: "small",
                                    marginTop: "1.25rem",
                                }}
                            >
                                This user specified effective R-value for the interior basement wall will be added to
                                all basement interior wall components in the model individually. The resulting effective
                                R-values may not be equal across multiple components, depending on their starting
                                points.
                                <br />
                                <br />
                                The initial effective R-value for each component is determined using the parallel path
                                calculation method, aligned with F280 and the National Building Code, and may differ
                                slightly from HOT2000's calculation method.
                            </div>
                        )}
                    </>
                )}
            </>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    setInitCode: async (code, type) => {
        return dispatch(setInitCode(code, type));
    },
    updateCodeDrawer: (updates) => dispatch(updateCodeDrawer({ updates })),
    updateCompositeCalc: (updates) => dispatch(updateCompositeCalc(updates)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WallConstruction);
